<template>
    <page title="competitionApprovalEventClass" :textHeight="(WindowSize.y - 272) + 'px'">
        <template #pageContainer>
			<v-form ref="form" v-model="valid">
				<v-row><v-col>
					<v-autocomplete outlined dense
						hide-details="auto"
						:readonly="isViewMode"
						:label="$t('text.classes')"
						:clearable="!isViewMode"
						:multiple="true"
						:items="Classes"
						item-text="longname"
						item-value="definitionClassId"
						return-object
						v-model="classes"
						@change="classSelectionChanged">
						<template v-slot:selection="{item}">
						<v-chip
							class="ma-1"
							close
							@click:close="removeClass(item.definitionClassId)"
							color="grey"
							text-color="black"
							dark
							label
							outlined>
							{{ item.longname }}
							</v-chip>
						</template>
					</v-autocomplete>
				</v-col></v-row>
				<v-row v-if="dlvEventsAllowed"><v-col>
					<v-autocomplete outlined dense
						hide-details="auto"
						:readonly="isViewMode"
						:disabled="!hasSelectedClasses"
						:clearable="!isViewMode"
						:label="$t('text.events')"
						:multiple="true"
						:items="Events"
						item-text="longname"
						item-value="definitionClassId"
						return-object
						v-model="events"
						@change="eventSelectionChanged">
						<template v-slot:selection="{item}">
						<v-chip
							class="ma-1"
							close
							@click:close="removeEvent(item.definitionEventId)"
							color="grey"
							text-color="black"
							dark
							label
							outlined>
							{{ item.longname }}
							</v-chip>
						</template>
					</v-autocomplete>
				</v-col></v-row>
				<v-row v-if="events.length != 0 && !isViewMode"><v-col>
					<v-alert
						dense
						outlined
						type="error">
						{{$t('text.hintNeedProtocol')}}
					</v-alert>
				</v-col></v-row>
				<v-row v-if="customEventsAllowed">
					<v-col cols="12" sm="10" lg="10" v-if="!isViewMode">
						<v-autocomplete outlined dense
							hide-details="auto"
							:readonly="isViewMode"
							:disabled="!hasSelectedClasses"
							:clearable="!isViewMode"
							:label="$t('text.customEvents')"
							return-object
							:items="customEvents"
							v-model="cEvent">
						</v-autocomplete>
					</v-col>
					<v-col cols="12" sm="2" lg="2" align="right">
							<v-btn
								v-if="!isViewMode"
								class="mr-4"
								:disabled="!cEvent"
								@click="customEventSelected">
								{{ $t(`text.apply`) }}
							</v-btn>
					</v-col>
				</v-row>
				<template v-if="cEventCollection.length !== 0">
					<v-row dense v-for="(c, i) in cEventCollection" :key="i">
						<v-col cols="12" sm="1" lg="1">
							</v-col>
						<v-col cols="12"  sm="5" lg="5">
							<v-text-field outlined dense
								hide-details="auto"
								:readonly="isViewMode"
								class="mt-0 pt-0"
								:rules="[rules.notEmpty, rules.maxLen200]"
								:clearable="!isViewMode"
								v-model="c.longname"
								:label="$t('text.name')">
							</v-text-field>
						</v-col>
						<v-col cols="12"  sm="4" lg="4">
							<v-text-field outlined dense v-if="c.measurement !== 'h'"
								hide-details="auto"
								:readonly="isViewMode"
								class="mt-0 pt-0"
								:rules="[rules.notEmpty]"
								type="number"
								:clearable="!isViewMode"
								v-model.number="c.courseLength"
								:label="`${$t('text.courseLength')} - (${$t('text.inKMeter')})`">
							</v-text-field>
							<v-text-field outlined dense v-if="c.measurement === 'h'"
								hide-details="auto"
								:clearable="!isViewMode"
								class="mt-0 pt-0"
								v-model="c.courseLength"
								:label="`${$t('text.duration')} - (${$t('text.timeFormat')})`"
								:rules="[rules.notEmpty, rules.isTime]"
								required
								@keyup="formatTime(i)">
						</v-text-field>
						</v-col>
						<v-col cols="12"  sm="2" lg="2" align="left">
							<v-btn
								v-if="!isViewMode"
								class="mr-4"
								@click="deleteCustomEvent(i)">
								<v-icon>mdi-delete</v-icon>
							</v-btn>
						</v-col>
					</v-row>
				</template>
				<template v-if="events.length !== 0">
					<template v-for="(e, i) in events">
						<v-row :key="e.definitionEventId">
							<v-col cols="12"  sm="5" lg="5">
								<b>{{ $t(`text.measurementProtocol`) }} ({{e.longname}})</b>
							</v-col>
						</v-row>
						<v-row :key="e.definitionEventId+'-data'" align="center">
							<v-col cols="12"  sm="2" lg="2">
								<v-menu
									v-model="dateMenu[i]"
									:disabled="isViewMode"
									:close-on-content-click="false"
									max-width="290">
									<template #activator="{ on }">
										<v-text-field outlined dense
											hide-details="auto"
											class="mt-0 pt-0"
											:value="formattedDate(e.measurementProtocol)"
											:clearable="!isViewMode"
											:label="$t('text.date')"
											readonly
											v-on="on"
											@click:clear="e.measurementProtocol = null">
										</v-text-field>
									</template>
									<v-date-picker
										locale="DE-de"
										v-model="e.measurementProtocol"
										@change="dateMenu[i] = false">
									</v-date-picker>
								</v-menu>
							</v-col>
							<v-col cols="12"  sm="2" lg="2">
								<c-checkbox v-model="e.protocolAvailable" :readonly="isViewMode" :text="$t('text.protocollAtDlvLv')"></c-checkbox>
							</v-col>
						</v-row>
					</template>
				</template>
            </v-form>
            <spinner :loading="loading"></spinner>
        </template>
		<template #pageActions>
			<pop-up v-model="confirmOverwrite"
				:show="showConfirmOverwrite"
				title="dialogText.reallySave"
				text="editCompApprovalHINT"
				:props="confirmSaveData"
				type="confirm"
				width="70%"
				@close="onConfirmClose">
			</pop-up>
			<v-row>
				<v-col>
					<v-btn
						class="mr-4"
						v-if="isViewMode"
						@click="backToOverview">
						{{ $t(`text.backToOverview`) }}
					</v-btn>
					<v-btn
						v-if="isViewMode"
						class="mr-4"
						@click="saveAndNext">
						{{ $t(`text.next`) }}
					</v-btn>
					<v-btn
						v-if="!isViewMode"
						class="mr-4"
						:disabled="loading"
						@click="save">
						{{ $t(`text.save`) }}
					</v-btn>
					<v-btn
						v-if="!isViewMode"
						class="mr-4"
						:disabled="loading"
						@click="saveAndNext">
						{{ $t(`text.createAndNext`) }}
					</v-btn>

					<v-btn
						:disabled="loading"
						class="mr-4"
						@click="back">
						{{ $t(`text.backButton`) }}
					</v-btn>
				</v-col>
			</v-row>
        </template>
    </page>
</template>

<script>
//* * ToDo: Popup on deletCustom or Popup on Save, if customPopup was not agreed */
import { mapActions, mapGetters } from 'vuex';

import compApprovalActions from '../store/actions/compApproval';
import definitionsActions from '../store/actions/definitions';
import namespace from '../store/namespace';

import Page from '../general/Page.vue';
import Spinner from '../general/Spinner.vue';
import CCheckbox from '../general/CCheckbox.vue';
import PopUp from '../general/PopUp.vue';

import getCustomEvents from '../models/customEvents';
import GUID from '../mixins/GUIDMixin';

import modelState from '../mixins/modelState';

export default {
	name: 'CompetitionApprovalEventClasses',
	mixins: [GUID, modelState],
	components: {
		Page,
		Spinner,
		CCheckbox,
		PopUp,
	},
	data() {
		return {
			valid: true,
			loading: false,
			classes: [],
			events: [],
			cEvent: null,
			cEventCollection: [],
			dateMenu: [],
			rules: {
				notEmpty: v => !!v || this.$i18n.t('text.errorEmpty'),
				isTime: v => /^([\d]+):?([0-5]?[0-9]):([0-5]?[0-9])$/.test(v) || this.$i18n.t('text.errorInvalidInput'),
				maxLen200: v => (v == null || v.length <= 200) || this.$i18n.t('text.toLong'),
			},
			pristineComp: {
				classes: [],
				events: [],
			},
			confirmOverwrite: 1,
			confirmSaveData: null,
			showConfirmOverwrite: false,
		};
	},
	async mounted() {
		this.loading = true;
		if (this.CompApprLinks.length === undefined || this.CompApprLinks.length === 0) {
			await this.GET_COMP_APPROVAL_LINKS();
		}
		if (this.DefinitionsLinks.length === undefined || this.DefinitionsLinks.length === 0) {
			await this.GET_DEFINITIONS_LINKS();
		}
		let link = this.DefinitionsLinks.find(x => x.rel === 'get_all_classes');
		if (link) {
			await this.GET_CLASSES(link);
		}
		link = this.DefinitionsLinks.find(x => x.rel === 'get_endurance_events');
		if (link) {
			await this.GET_ENDURANCE_EVENTS(link);
		}

		if (!this.Competition || this.Competition.id !== this.cGuid) {
			link = this.CompApprLinks.find(x => x.rel === 'get_by_id');
			if (link) {
				await this.GET_COMP_BY_ID({ link, cGuid: this.cGuid });
				this.readFromDTO();
			}
		} else {
			this.readFromDTO();
		}
		this.loading = false;
	},
	computed: {
		...mapGetters(['WindowSize']),
		...mapGetters(namespace.COMPETITIONAPPROVAL, ['CompApprLinks', 'Competition']),
		...mapGetters(namespace.DEFINITIONS, ['DefinitionsLinks', 'Classes', 'Events']),
		hasSelectedClasses() {
			return this.classes.length != 0;
		},
		customEventsAllowed() {
			if (!this.Competition) return null;
			return [1, 2].includes(this.Competition.competitionApprovalType);
		},
		dlvEventsAllowed() {
			if (!this.Competition) return null;
			return [0, 2].includes(this.Competition.competitionApprovalType);
		},
		cGuid() {
			return this.$route.params.cGuid || null;
		},
		hasSelectedEvents() {
			return this.events.length != 0;
		},
		customEvents() {
			return getCustomEvents(this);
		},
		isViewMode() {
			return this.$route.name === 'showcompAppEventClasses';
		},
		isPristine() {
			let events = this.dlvEventsAllowed ? this.events.map(x => x.longname + x.courseLength) : [];
			const finalCustomEvents = this.cEventCollection.map((x) => {
				let cl = x.courseLength;
				if (x.measurement === 'h') {
					const pairs = x.courseLength.split(':');
					cl = pairs[0] * 3600 + pairs[1] * 60;
				}
				cl *= 1000;
				return x.longname + cl;
			});
			if (this.customEventsAllowed) events = events.concat(finalCustomEvents);
			return JSON.stringify(this.pristineComp.classes) == JSON.stringify(this.classes.map(x => x.definitionClassId).sort())
				&& JSON.stringify(this.pristineComp.events) == JSON.stringify(events.sort());
		},
		isStatusResetting() {
			return !this.isPristine && this.Competition?.competitionState != 2;
		},
	},
	methods: {
		...mapActions(namespace.COMPETITIONAPPROVAL, [
			compApprovalActions.GET_COMP_APPROVAL_LINKS,
			compApprovalActions.GET_COMP_BY_ID,
			compApprovalActions.UPDATE_COMP_APPROVAL,
		]),
		...mapActions(namespace.DEFINITIONS, [
			definitionsActions.GET_DEFINITIONS_LINKS,
			definitionsActions.GET_CLASSES,
			definitionsActions.GET_ENDURANCE_EVENTS,
		]),
		formatTime(i) {
			let value = this.cEventCollection[i].courseLength;

			if (value) {
				value = value.replace(/^[0]+|[^0-9]/g, '');
				if (value != '') {
					value = value.padStart(4, 0);
					value = `${value.slice(0, -2)}:${value.slice(-2)}`;
				}
				this.cEventCollection[i].courseLength = value;
			}
		},
		formattedDate(date) {
			return date
				? this.$moment(date).format('DD.MM.YYYY')
				: '';
		},
		classSelectionChanged() {
			if (this.classes.length != 0) {
				this.tryRecoverClass();
				this.classes.sort((a, b) => (a.order > b.order ? 1 : -1));
			}
			this.eventSelectionChanged();
		},
		tryRecoverClass() {
			const defId = this.classes[this.classes.length - 1].definitionClassId;
			const found = this.Competition.classes.filter(x => x.definitionClassId == defId);
			if (found.length) {
				this.classes[this.classes.length - 1].id = found[0].id;
			}
		},
		removeClass(id) {
			const index = this.classes.findIndex(x => x.definitionClassId == id);
			if (index >= 0) this.classes.splice(index, 1);
		},
		eventSelectionChanged() {
			if (this.events.length != 0) {
				this.tryRecoverEvent();
				this.events.sort((a, b) => (a.order > b.order ? 1 : -1));
			}
		},
		tryRecoverEvent() {
			const defId = this.events[this.events.length - 1].definitionEventId;
			const found = this.Competition.events.filter(x => x.definitionEventId == defId);
			if (found.length) {
				[this.events[this.events.length - 1]] = found;
			}
		},
		removeEvent(id) {
			const index = this.events.findIndex(x => x.definitionEventId == id);
			if (index >= 0) this.events.splice(index, 1);
		},
		customEventSelected() {
			this.cEventCollection.push({
				longname: this.cEvent.text, courseLength: '', enduranceEventType: this.cEvent.value, id: this.EmptyGUID, measurement: this.cEvent.measurement,
			});
			this.cEvent = null;
		},
		deleteCustomEvent(i) {
			this.cEventCollection.splice(i, 1);
		},
		readFromDTO() {
			this.events = JSON.parse(JSON.stringify(this.Competition.events.filter(y => y.definitionEventId !== null)));// .map(x => x.definitionEventId);
			this.cEventCollection = JSON.parse(JSON.stringify(this.Competition.events.filter(y => y.definitionEventId === null)));
			this.cEventCollection.forEach((e) => {
				if (e.measurement === 'h') {
					const sec = (e.courseLength / 1000);
					const hours = Math.floor(sec / (60 * 60));

					const divisorForMinutes = sec % (60 * 60);
					const minutes = Math.floor(divisorForMinutes / 60);
					e.courseLength = `${String(hours).padStart(2, 0)}:${String(minutes).padStart(2, 0)}`;
				} else {
					e.courseLength /= 1000;
				}
			});
			this.classes = JSON.parse(JSON.stringify(this.Competition.classes));

			this.pristineComp.classes = this.classes.map(x => x.definitionClassId).sort();
			this.pristineComp.events = this.Competition.events.map(x => x.longname + x.courseLength).sort();
		},
		writeToDTO() {
			this.Competition.classes = this.classes;
			this.Competition.events = this.dlvEventsAllowed ? this.events : [];
			const finalCustomEvents = JSON.parse(JSON.stringify(this.cEventCollection));
			finalCustomEvents.forEach((e) => {
				if (e.measurement === 'h') {
					const pairs = e.courseLength.split(':');
					e.courseLength = pairs[0] * 3600 + pairs[1] * 60;
				}
				e.courseLength *= 1000;
			});
			if (this.customEventsAllowed) this.Competition.events = this.Competition.events.concat(finalCustomEvents);
		},
		async create() {
			this.loading = true;

			if (this.valid) {
				let link = null;
				link = this.CompApprLinks.find(x => x.rel === 'update_comp');

				this.writeToDTO();

				if (link) {
					try {
						this.modelState = {};
						await this.UPDATE_COMP_APPROVAL({ newCompApproval: this.Competition, link, cGuid: this.cGuid });
					} catch (e) {
						console.log(e);
						if (e.response.status === 422) {
							this.modelState = e.response.data;
							if (this.modelError()) {
								throw new Error(this.modelError());
							}
						}

						throw e;
					} finally {
						this.loading = false;
					}
				}
			}
			this.loading = false;
		},
		backToOverview() {
			this.$router.push({
				name: 'competitionapproval',
				params: { cGuid: this.cGuid },
			});
		},
		async save() {
			this.loading = true;
			this.$refs.form.validate();
			if (this.valid) {
				this.confirmSaveData = { name: 'competitionapproval' };
				await this.requestSave();
				// this.$router.push({ name: 'competitionapproval' });
			} else if (!this.valid && this.$refs.form.errorBag) {
				const id = Object.entries(this.$refs.form.errorBag).find(x => x[1] == true)[0];
				if (id) document.getElementById(`input-${id}`).scrollIntoView();
			}
			this.loading = false;
		},
		async saveAndNext() {
			this.loading = true;
			if (!this.isViewMode) {
				this.$refs.form.validate();
				if (this.valid) {
					this.confirmSaveData = {
						name: 'compAppEvClMatrix',
						params: { cGuid: this.cGuid },
					};

					await this.requestSave();
				} else if (!this.valid && this.$refs.form.errorBag) {
					const id = Object.entries(this.$refs.form.errorBag).find(x => x[1] == true)[0];
					if (id) document.getElementById(`input-${id}`).scrollIntoView();
				}
			} else {
				this.$router.push({
					name: 'showcompAppEvClMatrix',
					params: { cGuid: this.cGuid },
				});
			}
			this.loading = false;
		},
		back() {
			this.$router.go(-1);
		},
		async requestSave() {
			this.loading = true;
			if (this.isStatusResetting) {
				this.showConfirmOverwrite = true;
			} else {
				await this.create();
				this.$router.push(this.confirmSaveData);
			}
			this.loading = false;
		},
		async onConfirmClose(nextRoute) {
			this.loading = true;
			this.showConfirmOverwrite = false;
			this.confirmSaveData = null;
			if (this.confirmOverwrite) {
				await this.create();
				this.$router.push(nextRoute);
			}
			this.loading = false;
		},
	},
};
</script>
<style lang="scss" scoped>
table {
	display: block;
	overflow: auto;
	height:440px;
    max-width: fit-content;
}
.header-td {
	max-width: 25px;
    height: 180px;
	width: 1%;
    white-space: nowrap;
    vertical-align: bottom;
    padding-bottom: 5px;
    padding-right: 10px;
}

.side-header-td {
	width: 1%;
    white-space: nowrap;
    padding-right: 15px;
}

.header-element {
	transform: rotate(-90deg);
}

.grid-error {
	border: solid 1px red;
}
</style>
